import React from "react";
import { useTranslation } from "react-i18next";
import {
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ArrowRightOnRectangleIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@/shared/ui";

interface SessionErrorBoundaryProps {
  error: {
    type: string;
    message: string;
    retryable: boolean;
    retryCount: number;
  };
  onRetry?: () => void;
  onSignOut?: () => void;
  className?: string;
}

export const SessionErrorBoundary: React.FC<SessionErrorBoundaryProps> = ({
  error,
  onRetry,
  onSignOut,
  className = "",
}) => {
  const { t } = useTranslation();

  const getErrorTitle = () => {
    switch (error.type) {
      case "timeout":
        return t("errors.session.timeout.title", "Connection Timeout");
      case "network_error":
        return t("errors.session.network.title", "Network Error");
      case "invalid_token":
        return t("errors.session.invalidToken.title", "Session Expired");
      case "profile_load_failed":
        return t("errors.session.profileLoad.title", "Failed to Load Profile");
      case "permission_denied":
        return t("errors.session.permission.title", "Access Denied");
      default:
        return t("errors.session.unknown.title", "Something Went Wrong");
    }
  };

  const getErrorMessage = () => {
    switch (error.type) {
      case "timeout":
        return t(
          "errors.session.timeout.message",
          "The request took too long to complete. Please check your internet connection and try again."
        );
      case "network_error":
        return t(
          "errors.session.network.message",
          "Unable to connect to the server. Please check your internet connection."
        );
      case "invalid_token":
        return t(
          "errors.session.invalidToken.message",
          "Your session has expired. Please sign in again."
        );
      case "profile_load_failed":
        return t(
          "errors.session.profileLoad.message",
          "We couldn't load your profile. Please try again."
        );
      case "permission_denied":
        return t(
          "errors.session.permission.message",
          "You don't have permission to access this resource."
        );
      default:
        return error.message || t(
          "errors.session.unknown.message",
          "An unexpected error occurred. Please try again."
        );
    }
  };

  const showSignOut = error.type === "invalid_token" || error.type === "permission_denied";
  const maxRetries = 3;

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${className}`}>
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 border border-red-200">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="p-4 bg-red-100 rounded-full">
            <ExclamationTriangleIcon className="w-12 h-12 text-red-600" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-juridico text-center mb-3">
          {getErrorTitle()}
        </h2>

        {/* Message */}
        <p className="text-tiber-700 text-center mb-6">
          {getErrorMessage()}
        </p>

        {/* Retry Count */}
        {error.retryable && error.retryCount > 0 && (
          <p className="text-sm text-titan text-center mb-4">
            {t("errors.session.retryAttempt", {
              current: error.retryCount,
              max: maxRetries,
            })}
          </p>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {error.retryable && error.retryCount < maxRetries && onRetry && (
            <Button
              variant="primary"
              onClick={onRetry}
              className="w-full bg-linear-to-r from-tiber-500 to-tiber-600 hover:from-tiber-600 hover:to-tiber-700"
              leftIcon={<ArrowPathIcon className="w-5 h-5" />}
            >
              {t("errors.session.retry", "Try Again")}
            </Button>
          )}

          {showSignOut && onSignOut && (
            <Button
              variant="secondary"
              onClick={onSignOut}
              className="w-full"
              leftIcon={<ArrowRightOnRectangleIcon className="w-5 h-5" />}
            >
              {t("errors.session.signOut", "Sign Out")}
            </Button>
          )}

          {error.retryCount >= maxRetries && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800 text-center">
                {t(
                  "errors.session.maxRetries",
                  "Maximum retry attempts reached. Please refresh the page or contact support if the problem persists."
                )}
              </p>
            </div>
          )}
        </div>

        {/* Additional Help */}
        <div className="mt-6 pt-6 border-t border-tiber-200">
          <p className="text-xs text-titan text-center">
            {t(
              "errors.session.needHelp",
              "Need help? Contact our support team"
            )}
          </p>
        </div>
      </div>
    </div>
  );
};
